class AudioManager {
  constructor() {
    this.ctx = null;
    this.isMuted = false;
    this.volume = 0.5;
    this.currentTrack = null;
    this.intervalId = null;

    // HTML5 Audio for the provided House of the Dragon battle track
    this.hodAudio = null;
    this.hodFadeInterval = null;

    // Opening Rooftop Scene Ambient Audio Elements & Timers
    this.rooftopTrafficAudio = null;
    this.rooftopCrowdAudio = null;
    this.rooftopHornsAudio = null;
    this.rooftopBirdTimer = null;

    // Final Bird Scene Ambient Nodes & Timers
    this.gardenBreezeNode = null;
    this.gardenBreezeGain = null;
    this.gardenBirdTimer = null;

    // Shared Soft Emotional Music (Rooftop Scene + Final Bird Scene)
    this.sharedMusicInterval = null;
    this.sharedMusicStep = 0;

    // Active nodes tracking for clean teardown
    this.activeNodes = new Set();
    this.noiseBuffers = {};
  }

  init() {
    if (!this.ctx) {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (AudioCtx) {
        this.ctx = new AudioCtx();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {});
    }
  }

  // Generate loopable noise buffer (brown/pink) for traffic rumble and gentle breeze
  getNoiseBuffer(type = 'brown') {
    if (this.noiseBuffers[type]) return this.noiseBuffers[type];
    if (!this.ctx) this.init();
    if (!this.ctx) return null;

    const bufferSize = this.ctx.sampleRate * 3; // 3-second loop
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const output = buffer.getChannelData(0);

    let lastOut = 0.0;
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;

    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      if (type === 'brown') {
        // Brown noise: deep warm low rumble
        output[i] = (lastOut + (0.02 * white)) / 1.02;
        lastOut = output[i];
        output[i] *= 3.5;
      } else {
        // Pink noise: natural outdoor air / breeze
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.1538520;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.0168980;
        output[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
        b6 = white * 0.115926;
      }
    }

    this.noiseBuffers[type] = buffer;
    return buffer;
  }

  // ==========================================
  // GENERAL SOUND EFFECTS & FEEDBACK
  // ==========================================
  playBeep(freq = 440, type = 'sine', duration = 0.1, gainVal = 0.15) {
    if (this.isMuted) return;
    this.init();
    if (!this.ctx) return;
    try {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, this.ctx.currentTime);
      gain.gain.setValueAtTime(gainVal * this.volume, this.ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + duration);
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start();
      osc.stop(this.ctx.currentTime + duration);
    } catch (e) {
      console.warn("Audio error:", e);
    }
  }

  playDialogueBlip(speaker = '') {
    if (this.isMuted) return;
    let freq = 520;
    let type = 'sine';
    if (speaker.includes('Queen') || speaker.includes('Emily') || speaker.includes('Labonno')) {
      freq = 640 + Math.random() * 60; // Sweet higher melodic chime
      type = 'sine';
    } else if (speaker.includes('Knight') || speaker.includes('Talha')) {
      freq = 420 + Math.random() * 40; // Warm resonant mid-tone
      type = 'triangle';
    } else if (speaker.includes('Advisor') || speaker.includes('Champion') || speaker.includes('Council')) {
      freq = 280 + Math.random() * 30; // Deep medieval tone
      type = 'sawtooth';
    } else {
      freq = 520 + Math.random() * 40; // Narration
      type = 'sine';
    }
    this.playBeep(freq, type, 0.035, 0.035);
  }

  playQuestUpdate() {
    if (this.isMuted) return;
    this.init();
    const notes = [440, 554.37, 659.25, 880]; // A major fanfare
    notes.forEach((n, i) => {
      setTimeout(() => this.playBeep(n, 'triangle', 0.18, 0.12), i * 80);
    });
  }

  playClick() {
    this.playBeep(800, 'triangle', 0.05, 0.1);
  }

  playNotification() {
    if (this.isMuted) return;
    this.init();
    if (!this.ctx) return;
    [587.33, 880, 1174.66].forEach((f, idx) => {
      setTimeout(() => this.playBeep(f, 'sine', 0.18, 0.15), idx * 120);
    });
  }

  playRomanticChime() {
    if (this.isMuted) return;
    this.init();
    const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
    notes.forEach((n, i) => {
      setTimeout(() => this.playBeep(n, 'sine', 0.35, 0.2), i * 150);
    });
  }

  playSwordSlash() {
    if (this.isMuted) return;
    this.playBeep(260, 'sawtooth', 0.12, 0.25);
    setTimeout(() => this.playBeep(180, 'sawtooth', 0.08, 0.15), 30);
  }

  playPunch() {
    if (this.isMuted) return;
    this.playBeep(200, 'sawtooth', 0.09, 0.28);
    setTimeout(() => this.playBeep(100, 'triangle', 0.12, 0.2), 25);
  }

  playJump() {
    if (this.isMuted) return;
    this.playBeep(320, 'sine', 0.1, 0.15);
    setTimeout(() => this.playBeep(480, 'sine', 0.08, 0.1), 35);
  }

  playCastleRumble() {
    if (this.isMuted) return;
    this.playBeep(70, 'sawtooth', 0.6, 0.3);
  }

  playDash() {
    if (this.isMuted) return;
    this.playBeep(480, 'sine', 0.08, 0.22);
    setTimeout(() => this.playBeep(240, 'triangle', 0.12, 0.18), 30);
  }

  playParry() {
    if (this.isMuted) return;
    // Resonant metallic deflection ring
    this.playBeep(880, 'sine', 0.22, 0.35);
    setTimeout(() => this.playBeep(1320, 'sine', 0.3, 0.3), 20);
    setTimeout(() => this.playBeep(1760, 'triangle', 0.25, 0.2), 50);
  }

  playBossRoar() {
    if (this.isMuted) return;
    this.playBeep(55, 'sawtooth', 0.8, 0.45);
    setTimeout(() => this.playBeep(80, 'sawtooth', 0.6, 0.4), 80);
    setTimeout(() => this.playBeep(45, 'sawtooth', 0.9, 0.5), 200);
  }

  playSonicBlade() {
    if (this.isMuted) return;
    this.playBeep(620, 'sawtooth', 0.12, 0.25);
    setTimeout(() => this.playBeep(420, 'sine', 0.14, 0.2), 30);
  }

  // ==========================================
  // SHARED SOFT, CALM, EMOTIONAL BACKGROUND MUSIC
  // (Identically shared between Opening Rooftop & Final Bird Scene)
  // ==========================================
  playSharedEmotionalMusic() {
    this.init();
    if (this.sharedMusicInterval) return;

    // Warm, emotional chord progression in F Major / D Minor:
    // Bar 1: Fmaj7  - [F3, A3, C4, E4]
    // Bar 2: Dm9    - [D3, A3, C4, F4, E5]
    // Bar 3: Bbmaj7 - [Bb2, F3, A3, D4, F4]
    // Bar 4: Csus4  - [C3, G3, C4, E4, G4]
    const chords = [
      [174.61, 220.00, 261.63, 329.63, 440.00], // Fmaj7
      [146.83, 220.00, 261.63, 349.23, 659.25], // Dm9
      [116.54, 174.61, 220.00, 293.66, 349.23], // Bbmaj7
      [130.81, 196.00, 261.63, 329.63, 392.00]  // Cadd9 / Csus
    ];

    // Delicate high melodic celesta / piano counter-notes
    const melodyNotes = [
      523.25, 0, 659.25, 587.33,
      440.00, 523.25, 0, 392.00,
      349.23, 440.00, 523.25, 0,
      392.00, 329.63, 349.23, 0
    ];

    this.sharedMusicStep = 0;
    const tempo = 650; // Gentle, reflective tempo

    this.sharedMusicInterval = setInterval(() => {
      if (this.isMuted || !this.ctx || this.ctx.state !== 'running') return;

      const chordIdx = Math.floor((this.sharedMusicStep % 16) / 4);
      const chord = chords[chordIdx];
      const arpeggioNote = chord[this.sharedMusicStep % chord.length];

      // Play soft warm piano / rhodes arpeggio
      this.playVelvetPianoNote(arpeggioNote, 1.4, 0.024);

      // Play delicate melodic bell / celesta note on melody beats
      const melNote = melodyNotes[this.sharedMusicStep % melodyNotes.length];
      if (melNote > 0 && Math.random() < 0.85) {
        this.playVelvetPianoNote(melNote, 1.8, 0.016, true);
      }

      this.sharedMusicStep++;
    }, tempo);
  }

  playVelvetPianoNote(freq, duration = 1.4, baseGain = 0.025, isChime = false) {
    if (!this.ctx || this.isMuted) return;
    try {
      const now = this.ctx.currentTime;
      // Dual oscillator for warm acoustic body
      const osc1 = this.ctx.createOscillator();
      const osc2 = this.ctx.createOscillator();
      const filter = this.ctx.createBiquadFilter();
      const gain = this.ctx.createGain();

      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(freq, now);

      osc2.type = isChime ? 'sine' : 'triangle';
      osc2.frequency.setValueAtTime(freq * (isChime ? 2 : 1), now);

      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(isChime ? 2400 : 1200, now);

      const noteGain = baseGain * this.volume;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.linearRampToValueAtTime(noteGain, now + 0.035);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

      osc1.connect(filter);
      osc2.connect(filter);
      filter.connect(gain);
      gain.connect(this.ctx.destination);

      osc1.start(now);
      osc2.start(now);
      osc1.stop(now + duration);
      osc2.stop(now + duration);
    } catch (e) {}
  }

  stopSharedEmotionalMusic() {
    if (this.sharedMusicInterval) {
      clearInterval(this.sharedMusicInterval);
      this.sharedMusicInterval = null;
    }
  }

  // ==========================================
  // 1. OPENING ROOFTOP SCENE AUDIO
  // (Soft music, real city traffic audio, real crowd chatter, real car horns, birds)
  // ==========================================
  playRooftopSceneAudio() {
    this.stopAllAudio();
    this.init();
    this.currentTrack = 'rooftop_scene';

    // 1. Soft, calm, emotional background music (gentle & low-volume)
    this.playSharedEmotionalMusic();

    // 2. Real City Traffic Noise Asset (Continuous low rumble, rolling tires, passing cars)
    if (!this.rooftopTrafficAudio) {
      this.rooftopTrafficAudio = new Audio('/assets/audio/rooftop_traffic.wav');
      this.rooftopTrafficAudio.loop = true;
      this.rooftopTrafficAudio.preload = 'auto';
    }
    this.rooftopTrafficAudio.volume = this.isMuted ? 0 : 0.38 * this.volume;
    this.rooftopTrafficAudio.currentTime = 0;
    this.rooftopTrafficAudio.play().catch(e => console.warn("Rooftop traffic autoplay:", e));

    // 3. Real Distant People Talking & Busy City Crowd Asset (Continuous murmur & distant conversations)
    if (!this.rooftopCrowdAudio) {
      this.rooftopCrowdAudio = new Audio('/assets/audio/rooftop_crowd_voices.wav');
      this.rooftopCrowdAudio.loop = true;
      this.rooftopCrowdAudio.preload = 'auto';
    }
    this.rooftopCrowdAudio.volume = this.isMuted ? 0 : 0.34 * this.volume;
    this.rooftopCrowdAudio.currentTime = 0;
    this.rooftopCrowdAudio.play().catch(e => console.warn("Rooftop crowd autoplay:", e));

    // 4. Real City Car Horns Asset (Occasional realistic horns from roads below)
    if (!this.rooftopHornsAudio) {
      this.rooftopHornsAudio = new Audio('/assets/audio/rooftop_car_horns.wav');
      this.rooftopHornsAudio.loop = true;
      this.rooftopHornsAudio.preload = 'auto';
    }
    this.rooftopHornsAudio.volume = this.isMuted ? 0 : 0.44 * this.volume;
    this.rooftopHornsAudio.currentTime = 0;
    this.rooftopHornsAudio.play().catch(e => console.warn("Rooftop horns autoplay:", e));

    // 5. Natural Rooftop Birds chirping (every 4.5 - 8.5s)
    const scheduleNextRooftopBird = () => {
      const delay = 4500 + Math.random() * 4500;
      this.rooftopBirdTimer = setTimeout(() => {
        if (this.currentTrack === 'rooftop_scene' && !this.isMuted) {
          this.playRooftopBirdChirp();
          scheduleNextRooftopBird();
        }
      }, delay);
    };
    scheduleNextRooftopBird();
  }

  playRooftopBirdChirp() {
    if (!this.ctx || this.isMuted) return;
    try {
      const now = this.ctx.currentTime;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      const baseF = 3100 + Math.random() * 450;
      const peakF = baseF + 650 + Math.random() * 300;

      osc.type = 'sine';
      osc.frequency.setValueAtTime(baseF, now);
      osc.frequency.linearRampToValueAtTime(peakF, now + 0.04);
      osc.frequency.exponentialRampToValueAtTime(baseF * 0.88, now + 0.09);

      const chirpGain = 0.020 * this.volume;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.linearRampToValueAtTime(chirpGain, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.10);

      osc.connect(gain);
      gain.connect(this.ctx.destination);

      osc.start(now);
      osc.stop(now + 0.10);
    } catch (e) {}
  }

  stopRooftopSceneAudio() {
    if (this.rooftopBirdTimer) {
      clearTimeout(this.rooftopBirdTimer);
      this.rooftopBirdTimer = null;
    }
    [this.rooftopTrafficAudio, this.rooftopCrowdAudio, this.rooftopHornsAudio].forEach(audioEl => {
      if (audioEl) {
        try {
          audioEl.pause();
          audioEl.currentTime = 0;
        } catch (_) {}
      }
    });
    this.stopSharedEmotionalMusic();
  }

  // ==========================================
  // 2. FINAL ACTION SCENE — HOUSE OF THE DRAGON MUSIC
  // (Uses provided 'hod file bg.mp3' / 'hod fight bg.mp3')
  // ==========================================
  playHodBattleMusic() {
    this.init();
    if (this.currentTrack === 'hod_battle') return;
    this.stopAllAudio();
    this.currentTrack = 'hod_battle';

    if (!this.hodAudio) {
      this.hodAudio = new Audio();
      this.hodAudio.loop = true;
      this.hodAudio.preload = 'auto';

      // Provide both exact names for guaranteed cross-browser resolution
      const s1 = document.createElement('source');
      s1.src = encodeURI('/assets/audio/hod file bg.mp3');
      s1.type = 'audio/mpeg';

      const s2 = document.createElement('source');
      s2.src = encodeURI('/assets/audio/hod fight bg.mp3');
      s2.type = 'audio/mpeg';

      const s3 = document.createElement('source');
      s3.src = '/assets/audio/hod_fight_bg.mp3';
      s3.type = 'audio/mpeg';

      this.hodAudio.appendChild(s1);
      this.hodAudio.appendChild(s2);
      this.hodAudio.appendChild(s3);
    }

    if (this.hodFadeInterval) {
      clearInterval(this.hodFadeInterval);
      this.hodFadeInterval = null;
    }

    const targetVolume = this.isMuted ? 0 : 0.46 * this.volume;
    this.hodAudio.volume = 0;
    this.hodAudio.currentTime = 0;

    const playPromise = this.hodAudio.play();
    if (playPromise !== undefined) {
      playPromise.then(() => {
        // Smooth fade-in over 800ms
        let step = 0;
        const steps = 16;
        this.hodFadeInterval = setInterval(() => {
          step++;
          if (this.hodAudio && !this.isMuted) {
            this.hodAudio.volume = (targetVolume * step) / steps;
          }
          if (step >= steps) {
            clearInterval(this.hodFadeInterval);
            this.hodFadeInterval = null;
          }
        }, 50);
      }).catch(err => {
        console.warn("Autoplay notice for House of the Dragon battle track:", err);
      });
    }
  }

  stopHodBattleMusic(fadeTime = 800) {
    if (!this.hodAudio) return;
    if (this.hodFadeInterval) {
      clearInterval(this.hodFadeInterval);
      this.hodFadeInterval = null;
    }

    if (fadeTime <= 0 || this.hodAudio.paused) {
      this.hodAudio.pause();
      this.hodAudio.currentTime = 0;
      return;
    }

    const initialVol = this.hodAudio.volume;
    const steps = 16;
    let step = 0;
    const intervalTime = Math.max(20, Math.floor(fadeTime / steps));

    this.hodFadeInterval = setInterval(() => {
      step++;
      const factor = Math.max(0, 1 - step / steps);
      if (this.hodAudio) {
        this.hodAudio.volume = initialVol * factor;
      }
      if (step >= steps) {
        clearInterval(this.hodFadeInterval);
        this.hodFadeInterval = null;
        if (this.hodAudio) {
          this.hodAudio.pause();
          this.hodAudio.currentTime = 0;
        }
      }
    }, intervalTime);
  }

  // ==========================================
  // 3. FINAL BIRD SCENE
  // (Reuses exact soft music, natural cockatiels/birds, gentle breeze.
  //  STRICTLY NO horns, traffic, chatter, or city sounds!)
  // ==========================================
  playGardenSceneAudio() {
    this.stopAllAudio();
    this.init();
    this.currentTrack = 'garden_scene';

    // 1. Re-use the exact same soft, calm, emotional background music as the Opening Rooftop Scene!
    this.playSharedEmotionalMusic();

    if (!this.ctx || this.isMuted) return;

    try {
      // 2. Gentle peaceful outdoor breeze (lowpass filtered pink noise at 220Hz)
      const pinkBuf = this.getNoiseBuffer('pink');
      if (pinkBuf) {
        const breezeSource = this.ctx.createBufferSource();
        breezeSource.buffer = pinkBuf;
        breezeSource.loop = true;

        const breezeFilter = this.ctx.createBiquadFilter();
        breezeFilter.type = 'lowpass';
        breezeFilter.frequency.setValueAtTime(220, this.ctx.currentTime);
        breezeFilter.Q.setValueAtTime(0.8, this.ctx.currentTime);

        const breezeGain = this.ctx.createGain();
        breezeGain.gain.setValueAtTime(0.009 * this.volume, this.ctx.currentTime);

        breezeSource.connect(breezeFilter);
        breezeFilter.connect(breezeGain);
        breezeGain.connect(this.ctx.destination);

        breezeSource.start();
        this.gardenBreezeNode = breezeSource;
        this.gardenBreezeGain = breezeGain;
      }
    } catch (e) {
      console.warn("Garden breeze error:", e);
    }

    // 3. Lively, sweet cockatiel and garden birdsong (every 2.5 - 5s)
    const scheduleNextGardenBird = () => {
      const delay = 2500 + Math.random() * 2600;
      this.gardenBirdTimer = setTimeout(() => {
        if (this.currentTrack === 'garden_scene' && !this.isMuted) {
          this.playGardenBirdsong();
          scheduleNextGardenBird();
        }
      }, delay);
    };
    scheduleNextGardenBird();
  }

  playGardenBirdsong() {
    if (!this.ctx || this.isMuted) return;
    try {
      const now = this.ctx.currentTime;
      const type = Math.floor(Math.random() * 3);

      if (type === 0) {
        // Cockatiel rising melodic contact whistle (sweet upward slide then happy chirp)
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(2200, now);
        osc.frequency.exponentialRampToValueAtTime(3450, now + 0.16);
        osc.frequency.exponentialRampToValueAtTime(2900, now + 0.22);

        const birdGain = 0.032 * this.volume;
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.linearRampToValueAtTime(birdGain, now + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.24);

        osc.connect(gain);
        gain.connect(this.ctx.destination);
        osc.start(now);
        osc.stop(now + 0.24);
      } else if (type === 1) {
        // 3-note cheerful garden warbler trill
        [0, 0.08, 0.15].forEach((offset, idx) => {
          const osc = this.ctx.createOscillator();
          const gain = this.ctx.createGain();
          const freqs = [3300, 3900, 3600];
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freqs[idx], now + offset);
          osc.frequency.linearRampToValueAtTime(freqs[idx] + 250, now + offset + 0.05);

          const birdGain = 0.026 * this.volume;
          gain.gain.setValueAtTime(0.0001, now + offset);
          gain.gain.linearRampToValueAtTime(birdGain, now + offset + 0.015);
          gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.07);

          osc.connect(gain);
          gain.connect(this.ctx.destination);
          osc.start(now + offset);
          osc.stop(now + offset + 0.07);
        });
      } else {
        // Gentle cockatiel two-tone chirrup
        [0, 0.09].forEach((offset, idx) => {
          const osc = this.ctx.createOscillator();
          const gain = this.ctx.createGain();
          osc.type = 'sine';
          const f = idx === 0 ? 3000 : 3550;
          osc.frequency.setValueAtTime(f, now + offset);
          osc.frequency.exponentialRampToValueAtTime(f * 0.9, now + offset + 0.07);

          const birdGain = 0.028 * this.volume;
          gain.gain.setValueAtTime(0.0001, now + offset);
          gain.gain.linearRampToValueAtTime(birdGain, now + offset + 0.015);
          gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.08);

          osc.connect(gain);
          gain.connect(this.ctx.destination);
          osc.start(now + offset);
          osc.stop(now + offset + 0.08);
        });
      }
    } catch (e) {}
  }

  stopGardenSceneAudio() {
    if (this.gardenBirdTimer) {
      clearTimeout(this.gardenBirdTimer);
      this.gardenBirdTimer = null;
    }
    if (this.gardenBreezeNode) {
      try { this.gardenBreezeNode.stop(); } catch (e) {}
      this.gardenBreezeNode = null;
    }
    this.stopSharedEmotionalMusic();
  }

  // ==========================================
  // GENERAL THEMES & SYSTEM AUDIO
  // ==========================================
  playTheme(themeName) {
    this.init();
    if (this.currentTrack === themeName) return;
    this.stopAllAudio();
    this.currentTrack = themeName;
    if (this.isMuted) return;

    // Procedural musical arpeggiator / soft synthesizer for other chapters
    const themes = {
      menu: [261.63, 329.63, 392.00, 523.25, 392.00, 329.63], // C major romantic
      school: [293.66, 369.99, 440.00, 587.33, 440.00], // D major nostalgic
      bedroom: [220.00, 261.63, 329.63, 392.00], // A minor lo-fi
      fantasy: [174.61, 220.00, 261.63, 349.23, 261.63], // F dramatic modal
      escape: [146.83, 164.81, 174.61, 220.00, 196.00], // Fast tense
      garden: [329.63, 392.00, 493.88, 659.25, 493.88] // E major lush
    };

    const notes = themes[themeName] || themes.menu;
    let step = 0;
    const tempo = themeName === 'escape' ? 220 : 500;

    this.intervalId = setInterval(() => {
      if (!this.isMuted && this.ctx && this.ctx.state === 'running') {
        const freq = notes[step % notes.length];
        this.playSoftNote(freq, themeName === 'fantasy' || themeName === 'escape' ? 'sawtooth' : 'sine');
        step++;
      }
    }, tempo);
  }

  playSoftNote(freq, type = 'sine') {
    if (!this.ctx || this.isMuted) return;
    try {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, this.ctx.currentTime);
      gain.gain.setValueAtTime(0.04 * this.volume, this.ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + 0.6);
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start();
      osc.stop(this.ctx.currentTime + 0.6);
    } catch (e) {}
  }

  stopTheme() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // Clean, complete teardown of all audio layers to prevent any sound bleed between scenes
  stopAllAudio() {
    this.stopTheme();
    this.stopRooftopSceneAudio();
    this.stopGardenSceneAudio();
    this.stopHodBattleMusic(0);
    this.currentTrack = null;
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    if (this.isMuted) {
      if (this.hodAudio) {
        this.hodAudio.pause();
      }
      this.stopTheme();
      this.stopSharedEmotionalMusic();
      if (this.rooftopTrafficAudio) this.rooftopTrafficAudio.pause();
      if (this.rooftopCrowdAudio) this.rooftopCrowdAudio.pause();
      if (this.rooftopHornsAudio) this.rooftopHornsAudio.pause();
      if (this.gardenBreezeGain) this.gardenBreezeGain.gain.setValueAtTime(0, this.ctx?.currentTime || 0);
    } else {
      if (this.currentTrack === 'hod_battle' && this.hodAudio) {
        this.hodAudio.volume = 0.46 * this.volume;
        this.hodAudio.play().catch(() => {});
      } else if (this.currentTrack === 'rooftop_scene') {
        this.playRooftopSceneAudio();
      } else if (this.currentTrack === 'garden_scene') {
        this.playGardenSceneAudio();
      } else if (this.currentTrack) {
        const trk = this.currentTrack;
        this.currentTrack = null;
        this.playTheme(trk);
      }
    }
    return this.isMuted;
  }
}

export const audio = new AudioManager();
